import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Briefcase, Building2, MapPin, Calendar, X } from "lucide-react";

const STAGE_STYLE = {
    Applied: "bg-slate-100 text-slate-700 border-slate-300",
    Shortlist: "bg-blue-50 text-ikb border-blue-200",
    Interview: "bg-purple-50 text-purple-700 border-purple-200",
    Offer: "bg-amber-50 text-amber-700 border-amber-200",
    Hired: "bg-emerald-50 text-emerald-700 border-emerald-200",
    Rejected: "bg-rose-50 text-rose-700 border-rose-200",
    Withdrawn: "bg-slate-100 text-slate-500 border-slate-300",
};

function formatDate(iso) {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
    catch { return iso; }
}

export default function SeekerApplications() {
    const [apps, setApps] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        try { const { data } = await api.get("/seeker/applications"); setApps(data || []); }
        catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const withdraw = async (aid) => {
        if (!window.confirm("Withdraw this application?")) return;
        try {
            await api.post(`/seeker/applications/${aid}/withdraw`);
            setApps((arr) => arr.map((a) => a.id === aid ? { ...a, stage: "Withdrawn" } : a));
            toast.success("Application withdrawn");
        } catch (err) { toast.error(formatApiError(err)); }
    };

    return (
        <div className="max-w-5xl mx-auto p-3 sm:p-6 lg:p-8 space-y-8">
            <div>
                <div className="text-xs uppercase tracking-widest font-mono text-ikb">Tracker</div>
                <h1 className="font-display font-black text-2xl sm:text-4xl mt-2">My applications</h1>
                <p className="text-muted-foreground mt-2">Every role you've applied to, with live status updates from the hiring team.</p>
            </div>

            {loading ? (
                <div className="text-sm text-muted-foreground font-mono">Loading...</div>
            ) : apps.length === 0 ? (
                <div className="border border-dashed border-border p-16 text-center bg-white">
                    <h3 className="font-display font-bold text-xl">No applications yet</h3>
                    <p className="text-sm text-muted-foreground mt-2 mb-6">Find a role you love and apply with one click.</p>
                    <Link to="/seeker/jobs" className="text-xs font-mono uppercase tracking-widest border border-ikb text-ikb px-4 py-2 hover:bg-ikb hover:text-white inline-block transition-colors">
                        Browse jobs →
                    </Link>
                </div>
            ) : (
                <div className="border border-border bg-white divide-y divide-border">
                    {apps.map((a) => {
                        const stage = a.stage || "Applied";
                        const style = STAGE_STYLE[stage] || "bg-slate-100 text-slate-700 border-slate-300";
                        return (
                            <div key={a.id} data-testid={`seeker-app-${a.id}`} className="p-5 grid grid-cols-12 gap-4 items-center">
                                <div className="col-span-12 md:col-span-5">
                                    <div className="font-display font-bold text-base">{a.job?.title || "Job"}</div>
                                    <div className="flex items-center gap-3 text-xs font-mono text-muted-foreground mt-1 flex-wrap">
                                        <span className="inline-flex items-center gap-1"><Building2 className="w-3 h-3" /> {a.company?.name || "Company"}</span>
                                        {a.job?.location && (
                                            <>
                                                <span>·</span>
                                                <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {a.job.location}</span>
                                            </>
                                        )}
                                    </div>
                                </div>
                                <div className="col-span-6 md:col-span-3 text-xs font-mono text-muted-foreground">
                                    <Calendar className="w-3 h-3 inline-block mr-1 -mt-0.5" /> Applied {formatDate(a.created_at)}
                                </div>
                                <div className="col-span-6 md:col-span-2 text-xs font-mono text-muted-foreground">
                                    <Briefcase className="w-3 h-3 inline-block mr-1 -mt-0.5" /> {a.job?.employment_type || "—"}
                                </div>
                                <div className="col-span-12 md:col-span-2 text-right flex items-center justify-end gap-2 flex-wrap">
                                    <span className={`inline-block text-[10px] font-mono uppercase tracking-widest px-2 py-1 border ${style}`} data-testid={`seeker-app-stage-${a.id}`}>
                                        {stage}
                                    </span>
                                    {!["Withdrawn", "Hired", "Rejected"].includes(stage) && (
                                        <button
                                            type="button"
                                            onClick={() => withdraw(a.id)}
                                            className="text-[10px] font-mono uppercase tracking-widest border border-border hover:border-rose-400 hover:text-rose-700 hover:bg-rose-50 px-2 py-1 inline-flex items-center gap-1"
                                            data-testid={`withdraw-${a.id}`}
                                            title="Withdraw application"
                                        >
                                            <X className="w-3 h-3" /> Withdraw
                                        </button>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
